class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Re-render through the Section Rendering API: ~22 KB instead of the
  // ~150 KB of the full page, so the drawer refreshes several times faster.
  // Falls back to refetching the current page if the section can't be
  // resolved for any reason.
  renderContents(parsedState) {
    this.productId = parsedState && parsedState.id;

    const sectionUrl = `${window.location.pathname}?section_id=cart-drawer`;

    fetch(sectionUrl, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`section ${response.status}`))))
      .then((html) => {
        if (html.indexOf('CartDrawer') === -1) throw new Error('section without drawer');
        return html;
      })
      .catch(() => fetch(window.location.href, { credentials: 'same-origin' }).then((r) => r.text()))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newDrawerInner = doc.querySelector('#CartDrawer');
        const oldDrawerInner = document.querySelector('#CartDrawer');
        if (newDrawerInner && oldDrawerInner) {
          oldDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }

        // The drawer section alone doesn't carry the header badge, so read the
        // count from the cart state when we have it and fall back to the markup.
        const oldBadge = document.querySelector('#cart-icon-bubble .cart-count') || document.querySelector('.cart-count');
        const newBadge = doc.querySelector('#cart-icon-bubble .cart-count') || doc.querySelector('.cart-count');
        const drawerCount = doc.querySelector('#CartDrawer .cd-count-badge');
        if (oldBadge) {
          if (parsedState && typeof parsedState.item_count === 'number') {
            oldBadge.textContent = parsedState.item_count;
          } else if (newBadge) {
            oldBadge.textContent = newBadge.textContent;
          } else if (drawerCount) {
            oldBadge.textContent = drawerCount.textContent.trim();
          }
        }

        const drawerInnerWrap = this.querySelector('.drawer__inner');
        if (drawerInnerWrap) drawerInnerWrap.classList.remove('is-empty');
        if (parsedState && parsedState.item_count === 0) {
          this.classList.add('is-empty');
        } else {
          this.classList.remove('is-empty');
        }

        setTimeout(() => {
          const overlay = this.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', this.close.bind(this));
          this.open();
        });
      })
      .catch((e) => {
        console.error('[cart-drawer] renderContents refetch failed', e);
        this.open();
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
